/**
 * Rules for the Contact Us form: what counts as valid, how it is packaged for
 * POST /api/contact, and what to tell the visitor when sending fails.
 *
 * The form only ever reports success when the backend answers 2xx. Anything
 * else -- including the endpoint not existing yet -- is shown as not sent,
 * with the visitor's text kept so nothing they typed is lost.
 */
export const CONTACT_LIMITS = {
  name: 100,
  email: 254,
  company: 120,
  message: 2000,
  fileBytes: 10 * 1024 * 1024,
};

// PDFs, Word documents and images: what a visitor plausibly attaches to a
// question about an IPO. The backend must check again -- a browser's type and
// extension are both supplied by the visitor.
export const ACCEPTED_FILE_TYPES = {
  '.pdf': ['application/pdf'],
  '.doc': ['application/msword'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.png': ['image/png'],
  '.jpg': ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
};

export const FILE_ACCEPT_ATTRIBUTE = Object.keys(ACCEPTED_FILE_TYPES).join(',');

export const EMPTY_CONTACT = {
  name: '',
  email: '',
  company: '',
  message: '',
  // Left empty by people, filled in by bots that complete every field; the
  // backend discards submissions that carry it
  website: '',
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** An error message for the attachment, or null when it can be sent. */
export const validateContactFile = (file) => {
  if (!file) return null;
  const extension = (String(file.name || '').toLowerCase().match(/\.[a-z0-9]+$/) || [''])[0];
  const allowedTypes = ACCEPTED_FILE_TYPES[extension];
  // Some systems report no type for Word files, so an empty type is judged by extension alone
  if (!allowedTypes || (file.type && !allowedTypes.includes(file.type))) {
    return 'Attach a PDF, Word document, PNG or JPG.';
  }
  if (!file.size) return 'That file is empty.';
  if (file.size > CONTACT_LIMITS.fileBytes) {
    return `That file is ${formatFileSize(file.size)}. Attach one under ${formatFileSize(CONTACT_LIMITS.fileBytes)}.`;
  }
  return null;
};

/** Field errors keyed by field name; an empty object means the form can be sent. */
export const validateContact = (values, file = null) => {
  const errors = {};
  const name = values.name.trim();
  const email = values.email.trim();

  if (!name) errors.name = 'Enter your name.';
  else if (name.length < 2) errors.name = 'Enter your full name.';
  else if (name.length > CONTACT_LIMITS.name) errors.name = `Keep your name under ${CONTACT_LIMITS.name} characters.`;

  if (!email) errors.email = 'Enter your email address so we can reply.';
  else if (email.length > CONTACT_LIMITS.email || !EMAIL.test(email)) errors.email = 'Enter a valid email address, like name@example.com.';

  if (values.company.trim().length > CONTACT_LIMITS.company) {
    errors.company = `Keep the company name under ${CONTACT_LIMITS.company} characters.`;
  }
  if (values.message.trim().length > CONTACT_LIMITS.message) {
    errors.message = `Keep your message under ${CONTACT_LIMITS.message} characters.`;
  }

  const fileError = validateContactFile(file);
  if (fileError) errors.file = fileError;
  return errors;
};

/** The multipart body POST /api/contact receives. */
export const buildContactFormData = (values, file = null, sourcePage = '') => {
  const body = new FormData();
  body.append('name', values.name.trim());
  body.append('email', values.email.trim());
  body.append('company', values.company.trim());
  body.append('message', values.message.trim());
  body.append('website', values.website);
  // Which page the visitor wrote from, for context when replying
  if (sourcePage) body.append('source_page', sourcePage);
  if (file) body.append('file', file, file.name);
  return body;
};

/**
 * What to tell the visitor when sending failed. `error.status` is the HTTP
 * status axiosClient attaches; it is absent when the request never got an answer.
 */
export const contactErrorMessage = (error) => {
  const status = error?.status;
  if (!status) {
    return 'We couldn’t reach our server. Check your connection and try again — your message has not been sent.';
  }
  if (status === 404 || status === 405 || status === 501) {
    return 'Our contact inbox isn’t connected yet, so your message was not sent. Please try again later.';
  }
  if (status === 413) return `That file is too large to send. Attach one under ${formatFileSize(CONTACT_LIMITS.fileBytes)}.`;
  if (status === 415) return 'That file type can’t be sent. Attach a PDF, Word document, PNG or JPG.';
  if (status === 429) return 'Too many messages from this connection. Please wait a few minutes and try again.';
  if (status === 400 || status === 422) return 'Some details weren’t accepted. Please check the form and try again.';
  return 'Our server couldn’t send your message. Please try again in a few minutes.';
};
